/** Comfort mode — an opt-in larger-text / bigger-tap-target reading mode (Phase 32, GUIDE-09).
 *
 * Mirrors `useTheme`: a single `<html data-comfort="large">` attribute is the source of truth
 * (see styles/global.css, where every comfort rule is scoped under that attribute selector). The
 * user's choice persists to localStorage and is OFF by default, so the standard workstation layout
 * is untouched unless a chemist turns it on. SSR/no-DOM safe (all DOM/storage access is guarded).
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "prisma.comfort";

/** Read the persisted choice; defaults to false (comfort off). */
export function readComfort(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "large";
  } catch {
    /* localStorage unavailable — fall back to off */
    return false;
  }
}

/** Reflect the boolean onto `<html data-comfort>` ("large" when on, removed when off). */
function applyComfort(comfort: boolean): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-comfort", comfort ? "large" : "");
}

export interface ComfortControl {
  comfort: boolean;
  setComfort: (v: boolean) => void;
  toggle: () => void;
}

export function useComfort(): ComfortControl {
  const [comfort, setComfortState] = useState<boolean>(() => readComfort());

  // Apply on mount and whenever the value changes, keeping the attribute in sync.
  useEffect(() => {
    applyComfort(comfort);
  }, [comfort]);

  // Keep every mounted useComfort instance (and the persisted choice) in sync. A `storage` event
  // fires in OTHER tabs/windows when localStorage changes; we re-read so two toggles never drift.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== STORAGE_KEY) return; // ignore unrelated keys (null = cleared)
      setComfortState(readComfort());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setComfort = useCallback((next: boolean) => {
    try {
      // Mirror useTheme: persist the chosen value, and CLEAR the key when turning comfort off (rather
      // than writing a sentinel) so storage stays consistent with how `readComfort` reads it.
      if (next) localStorage.setItem(STORAGE_KEY, "large");
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore persistence failure — the in-memory choice still applies for this session */
    }
    setComfortState(next);
  }, []);

  // Toggle off the REACT STATE, not localStorage: in private mode where setItem throws, storage can
  // get stuck "on" — deriving from state means the next toggle still flips it off (no desync).
  const toggle = useCallback(() => {
    setComfortState((prev) => {
      const next = !prev;
      try {
        if (next) localStorage.setItem(STORAGE_KEY, "large");
        else localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* persistence failure — the in-memory flip still applies and stays correct */
      }
      return next;
    });
  }, []);

  return { comfort, setComfort, toggle };
}
